/**
 * Choosing which sites and cameras a person can see.
 *
 * The gap this closes: an invitation used to grant the whole tenant, so a viewer
 * added for one client's house could see every client's cameras. For a product
 * whose stated threat is someone watching people's homes, that was the same
 * failure the sealed-credential design prevents externally, reproduced inside by
 * an invitation.
 *
 * Everything here is a courtesy. Access is enforced by row-level security in
 * Postgres, so a person outside their grants sees nothing regardless of what this
 * form does or fails to do.
 */

import { useEffect, useState } from 'react';

import {
  ApiError,
  fetchDevices,
  fetchSites,
  setUserAccess,
  type ApiDevice,
  type ApiSite,
  type ApiUser,
} from './api.ts';

type Scope = 'all' | 'selected';

export function EditAccess({
  user,
  onSaved,
  onCancel,
}: {
  user: ApiUser;
  onSaved: () => void;
  onCancel: () => void;
}): React.JSX.Element {
  const [sites, setSites] = useState<ApiSite[] | null>(null);
  const [devices, setDevices] = useState<ApiDevice[]>([]);
  const [scope, setScope] = useState<Scope>(user.access_scope ?? 'all');
  const [selected, setSelected] = useState<Set<string>>(new Set(user.site_ids ?? []));
  const [excluded, setExcluded] = useState<Set<string>>(new Set(user.excluded_camera_ids ?? []));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [siteList, deviceList] = await Promise.all([fetchSites(), fetchDevices()]);
        if (cancelled) return;
        setSites(siteList);
        setDevices(deviceList);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Could not load sites.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  function toggle(set: Set<string>, id: string): Set<string> {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  }

  async function save(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await setUserAccess(user.id, {
        scope,
        siteIds: scope === 'selected' ? [...selected] : [],
        // Only meaningful within granted sites; sending exclusions for a site
        // they cannot see would be state nobody can explain later.
        excludedCameraIds:
          scope === 'selected'
            ? [...excluded].filter((id) => camerasBySite.some((entry) => entry.cameraIds.includes(id) && selected.has(entry.siteId)))
            : [],
      });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save access.');
    } finally {
      setBusy(false);
    }
  }

  /** Cameras grouped by the site they sit at, via their device. */
  const camerasBySite = (sites ?? []).map((site) => {
    const atSite = devices.filter((device) => device.site_name === site.name);
    return {
      siteId: site.id,
      cameraIds: atSite.flatMap((device) => device.cameras.map((camera) => camera.id)),
      cameras: atSite.flatMap((device) =>
        device.cameras.map((camera) => ({ id: camera.id, name: camera.name ?? camera.id })),
      ),
    };
  });

  return (
    <div className="access">
      <h4>What {user.email} can see</h4>

      <div className="field">
        <label className="access__choice">
          <input
            type="radio"
            name={`scope-${user.id}`}
            checked={scope === 'all'}
            onChange={() => setScope('all')}
          />
          <span>
            <strong>Every site</strong>
            <span className="field__hint">
              Including sites added later. Right for your own staff who work across the whole
              estate.
            </span>
          </span>
        </label>

        <label className="access__choice">
          <input
            type="radio"
            name={`scope-${user.id}`}
            checked={scope === 'selected'}
            onChange={() => setScope('selected')}
          />
          <span>
            <strong>Only the sites I choose</strong>
            <span className="field__hint">
              A site added later is <em>not</em> included until you add it here — deliberately, so
              access never widens on its own.
            </span>
          </span>
        </label>
      </div>

      {scope === 'selected' ? (
        sites === null ? (
          <p className="empty">Loading sites…</p>
        ) : sites.length === 0 ? (
          <p className="notice notice--warn">There are no sites yet, so there is nothing to grant.</p>
        ) : (
          <ul className="access__sites">
            {camerasBySite.map((entry) => {
              const site = sites.find((s) => s.id === entry.siteId) as ApiSite;
              const granted = selected.has(site.id);

              return (
                <li key={site.id} className="access__site">
                  <label className="access__row">
                    <input
                      type="checkbox"
                      checked={granted}
                      onChange={() => setSelected(toggle(selected, site.id))}
                    />
                    <span>
                      <strong>{site.name}</strong>
                      {site.client_name ? (
                        <span className="access__client">{site.client_name}</span>
                      ) : null}
                    </span>
                  </label>

                  {/* Camera exceptions only appear once a site is granted —
                      offering them for a site nobody can see would be settings
                      with no effect, which is worse than none. */}
                  {granted && entry.cameras.length > 0 ? (
                    <ul className="access__cameras">
                      {entry.cameras.map((camera) => (
                        <li key={camera.id}>
                          <label className="access__row access__row--nested">
                            <input
                              type="checkbox"
                              checked={!excluded.has(camera.id)}
                              onChange={() => setExcluded(toggle(excluded, camera.id))}
                            />
                            <span>{camera.name}</span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )
      ) : null}

      {/* Said plainly, because it is the consequence people forget: narrowing
          access signs them out, and they will notice. */}
      <p className="form__hint">
        Saving ends this person’s current sessions. They sign in again with the access you have
        just set.
      </p>

      <div className="form__actions">
        <button type="button" className="btn btn--primary btn--sm" disabled={busy} onClick={() => void save()}>
          {busy ? 'Saving…' : 'Save access'}
        </button>
        <button type="button" className="btn btn--sm" onClick={onCancel}>
          Cancel
        </button>
      </div>

      {error ? (
        <p className="notice notice--error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
